import React, { useState, useImperativeHandle, forwardRef } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import {
  useCreateSSHKey,
  useDeleteSSHKey,
  useGetSSHKeys,
} from '@/services/sshkeys'
import { Trash2, Settings2, Copy, Key, Info, ExternalLink } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { Alert } from '@/components/ui/alert'
import { ConfirmationDialog } from '@/components/confirmation-dialog'
import { Link } from '@tanstack/react-router'
import { ErrorMessage } from '@/components/error-message'
import { LoadingMessage } from '@/components/loading-message'

const SSH_KEY_EXAMPLE = 'ssh-rsa AAAAB3NzaC1yc2EAAA...'

const FormSchema = z.object({
  sshKey: z
    .string()
    .refine((val) => /^ssh-(rsa|dss|ed25519|ecdsa) \S+/.test(val.trim()), {
      message:
        'Invalid SSH key. Key should start with ssh-rsa, ssh-dss, ssh-ed25519, or ssh-ecdsa.',
    }),
})

export interface SetupSSHKeysDialogRef {
  openDialog: () => void
}

interface SetupSSHKeysDialogProps {
  orgId: string | number
  onComplete?: () => void
  isInWarning?: boolean
}

export const SetupSSHKeysDialog = forwardRef<
  SetupSSHKeysDialogRef,
  SetupSSHKeysDialogProps
>(
  (
    { orgId, onComplete, isInWarning = false }: SetupSSHKeysDialogProps,
    ref
  ) => {
    const [open, setOpen] = useState(false) // State to control dialog open/close
    const [openDeleteDialog, setOpenDeleteDialog] = useState<number | boolean>(
      false
    )

    // Expose the openDialog method through the ref
    useImperativeHandle(ref, () => ({
      openDialog: () => {
        setOpen(true)
      },
    }))

    const form = useForm({
      resolver: zodResolver(FormSchema),
      defaultValues: {
        sshKey: '',
      },
    })

    const {
      data: sshKeys = [],
      isLoading: isKeysLoading,
      error: keysError,
    } = useGetSSHKeys() // Fetch SSH Keys

    const {
      mutateAsync: createSSHKey,
      isPending: isCreating,
      isSuccess,
    } = useCreateSSHKey()

    const { mutate: deleteSSHKey, isPending: isDeletingSSHKey } =
      useDeleteSSHKey()

    const onSubmit = async (values: z.infer<typeof FormSchema>) => {
      await createSSHKey(
        { alias: `key-${orgId}`, public_key: values.sshKey },
        {
          onSuccess: () => {
            form.reset()
            if (onComplete) {
              onComplete()
            }
          },
        }
      )
    }

    const handleDeleteSSHKey = () => {
      if (typeof openDeleteDialog === 'number') {
        deleteSSHKey(String(openDeleteDialog), {
          onSuccess: () => {
            setOpenDeleteDialog(false)
          },
        })
      }
    }

    const copyToClipboard = (text: string) => {
      navigator.clipboard
        .writeText(text)
        .then(() => toast.success('SSH key copied to clipboard'))
        .catch((err) => console.error('Could not copy text: ', err))
    }

    return (
      <Dialog
        open={open}
        onOpenChange={(value) => {
          setOpen(value)
        }}
      >
        {isInWarning ? (
          <DialogTrigger asChild>
            <Button
              variant="link"
              className="ssh-keys-dialog-trigger underline decoration-wavy px-0 ml-1 h-auto font-normal text-xs text-yellow-600 dark:text-yellow-400"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setOpen(true)
              }}
            >
              {sshKeys && sshKeys.length > 0
                ? 'Manage SSH Keys'
                : 'Add SSH Key'}
            </Button>
          </DialogTrigger>
        ) : (
          <span className="hidden"></span>
        )}

        <DialogContent
          className="sm:max-w-[600px]"
          onInteractOutside={(e) => {
            e.preventDefault()
          }}
        >
          <DialogHeader>
            <DialogTitle>Add SSH Keys</DialogTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Adding an SSH key allows you to securely access compute resources
              through the terminal using the corresponding private key. You can
              add a maximum of three separate SSH keys. The system automatically
              applies these keys during server deployment as an option.
            </p>
          </DialogHeader>

          {/* Add SSH Key Form */}
          <div className="border rounded-lg p-4 bg-muted/10 space-y-3 my-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Add a New SSH Key</h3>
              <a
                href="https://docs.github.com/en/authentication/connecting-to-github-with-ssh/generating-a-new-ssh-key-and-adding-it-to-the-ssh-agent"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs flex items-center gap-1 text-primary"
              >
                <ExternalLink className="h-3 w-3" /> How to generate SSH keys
              </a>
            </div>

            {sshKeys.length < 3 ? (
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <div className="flex items-center text-xs text-muted-foreground gap-1.5 mb-1">
                    <Info className="h-3 w-3" />
                    <span>Format: {SSH_KEY_EXAMPLE}</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Textarea
                      placeholder="Paste your public SSH key here"
                      {...form.register('sshKey')}
                      className="flex-1 font-mono text-xs"
                      rows={3}
                    />
                    <div className="flex justify-between items-center">
                      {form.formState.errors.sshKey?.message && (
                        <ErrorMessage
                          message={form.formState.errors.sshKey.message}
                          className="text-xs"
                          icon={false}
                        />
                      )}
                      <Button
                        type="submit"
                        disabled={isCreating || sshKeys.length >= 3}
                        size="sm"
                      >
                        {isCreating ? (
                          <span className="mr-2">
                            <LoadingMessage size="sm" />
                          </span>
                        ) : null}
                        {isCreating ? 'Adding...' : 'Add'}
                      </Button>
                    </div>
                  </div>
                </div>
              </form>
            ) : (
              <Alert className="text-xs py-2 bg-muted border-muted">
                <div className="flex items-center">
                  <Info className="h-4 w-4 mr-2" />
                  Maximum of 3 SSH keys reached. Delete an existing key to add a
                  new one.
                </div>
              </Alert>
            )}

            {isSuccess ? (
              <Alert className="text-xs py-2 bg-alert-info-background text-alert-info border-alert-info-border">
                <div className="flex items-center">
                  <svg
                    className="h-4 w-4 text-alert-info mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  SSH Key added successfully!
                </div>
              </Alert>
            ) : null}
          </div>

          {/* SSH Keys List Section - Only show when loading or when there are keys */}
          {(isKeysLoading || sshKeys.length > 0) && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">
                  Your SSH Keys ({sshKeys.length}/3)
                </h3>
                {sshKeys.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    Click <Copy className="h-3 w-3 inline" /> to copy
                  </span>
                )}
              </div>

              {/* List of SSH Keys */}
              <div className="space-y-3">
                {isKeysLoading ? (
                  <div className="flex justify-center py-4">
                    <LoadingMessage message="Loading SSH keys..." />
                  </div>
                ) : (
                  sshKeys.map((key) => (
                    <div
                      key={key.key_id}
                      className="group flex items-center space-x-2 rounded-md border p-2 bg-muted/5 hover:bg-muted/10 transition-colors"
                    >
                      <div className="bg-primary/10 p-1.5 rounded">
                        <Key className="h-4 w-4 text-primary" />
                      </div>
                      <p className="flex-1 font-mono text-xs max-w-[400px] truncate">
                        {key.public_key}
                      </p>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 opacity-60 hover:opacity-100"
                        onClick={() => copyToClipboard(key.public_key)}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive opacity-60 hover:opacity-100"
                        onClick={() => setOpenDeleteDialog(key.key_id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Display Fetch Errors */}
          {keysError?.message && (
            <ErrorMessage message={keysError.message} className="mt-4" />
          )}

          {/* Footer with buttons */}
          <div className="flex justify-between items-center mt-4">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1.5"
              asChild
            >
              <Link to={`/${orgId}/dashboard/sshkeys`}>
                <Settings2 className="h-3.5 w-3.5" />
                Manage All Keys
              </Link>
            </Button>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>

          {/* Delete Confirmation Dialog */}
          <ConfirmationDialog
            open={!!openDeleteDialog}
            onOpenChange={(open) => {
              if (!isDeletingSSHKey) {
                setOpenDeleteDialog(open ? openDeleteDialog : false)
              }
            }}
            title="Confirm Delete"
            description="Are you sure you want to delete this SSH Key? This action cannot be undone."
            buttons={{
              confirm: {
                label: 'Delete',
                isDestructive: true,
                isLoading: isDeletingSSHKey,
              },
              cancel: {
                label: 'Cancel',
              },
            }}
            onConfirm={handleDeleteSSHKey}
          />
        </DialogContent>
      </Dialog>
    )
  }
)
